import cx from "clsx"
import { FieldOptionsSchema, type FieldOption } from "shared"
import PlusIcon from "@rawa/ui/assets/icon-plus.svg?react"
import TrashIcon from "@rawa/ui/assets/icon-trash.svg?react"
import { AdminButton } from "@rawa/ui/AdminButton"
import { nextOptionValue } from "./nextOptionValue"
import styles from "./OptionsEditor.module.css"

/**
 * The choices a control offers, edited in place: what a practitioner sees,
 * and the value it stands for. Both are required, and the value is the
 * option's identity, so a blank name, a blank value, a value another option
 * already has in any case, or one that breaks the value rules is flagged where
 * it is typed, and the schema refuses the module until it is put right. The
 * last option cannot be removed: a list with nothing to choose has nothing to
 * show.
 *
 * The eyebrow names the section and the legend names the group; the eyebrow
 * sits outside the fieldset so assistive tech hears the legend alone.
 */
export function OptionsEditor({
  eyebrow,
  legend,
  options,
  onChange,
}: {
  eyebrow: string
  legend: string
  options: ReadonlyArray<FieldOption>
  onChange: (options: FieldOption[]) => void
}) {
  const place = (index: number) => `Option ${index + 1}`
  const change = (index: number, edit: Partial<FieldOption>) =>
    onChange(
      options.map((held, i) => (i === index ? { ...held, ...edit } : held)),
    )

  // Every problem as the schema the server applies reports it, at the option
  // and property it belongs to.
  const issues = FieldOptionsSchema.safeParse(options).error?.issues ?? []
  const problemsAt = (index: number, property: "label" | "value") =>
    issues.filter(
      (issue) => issue.path[0] === index && issue.path[1] === property,
    )
  const unnamed = (index: number) => problemsAt(index, "label").length > 0
  const flagged = (index: number) => problemsAt(index, "value").length > 0
  const messagesFor = (property: "label" | "value") => [
    ...new Set(
      issues
        .filter((issue) => issue.path[1] === property)
        .map((issue) => issue.message),
    ),
  ]
  const labelProblems = messagesFor("label")
  const valueProblems = messagesFor("value")

  return (
    <div className={styles.card}>
      <p className={styles.eyebrow}>{eyebrow}</p>
      <fieldset className={styles.group}>
        <legend className={styles.legend}>{legend}</legend>
        <p className={styles.hint}>
          Add, rename or remove the choices a practitioner can pick from.
        </p>
        <ul className={styles.options}>
          {options.map((option, index) => (
            <li key={index} className={styles.row}>
              <input
                type="text"
                className={cx(styles.option, {
                  [styles.invalid]: unnamed(index),
                })}
                aria-label={place(index)}
                aria-invalid={unnamed(index)}
                aria-describedby={unnamed(index) ? "option-labels" : undefined}
                placeholder={place(index)}
                value={option.label}
                onChange={(e) => change(index, { label: e.target.value })}
              />
              <input
                type="text"
                className={cx(styles.value, {
                  [styles.invalid]: flagged(index),
                })}
                aria-label={`${place(index)} value`}
                aria-invalid={flagged(index)}
                aria-describedby={flagged(index) ? "option-values" : undefined}
                placeholder="Value"
                value={option.value}
                onChange={(e) => change(index, { value: e.target.value })}
              />
              <button
                type="button"
                className={styles.remove}
                aria-label={`Remove ${place(index).toLowerCase()}`}
                disabled={options.length === 1}
                onClick={() => onChange(options.filter((_, i) => i !== index))}
              >
                <TrashIcon aria-hidden="true" />
              </button>
            </li>
          ))}
        </ul>
        {labelProblems.length > 0 && (
          <div id="option-labels" className={styles.problems}>
            {labelProblems.map((problem) => (
              <p key={problem} className={styles.problem}>
                {problem}
              </p>
            ))}
          </div>
        )}
        {valueProblems.length > 0 && (
          <div id="option-values" className={styles.problems}>
            {valueProblems.map((problem) => (
              <p key={problem} className={styles.problem}>
                {problem}
              </p>
            ))}
          </div>
        )}
        <AdminButton
          variant="outline"
          className={styles.add}
          onClick={() =>
            onChange([
              ...options,
              { label: place(options.length), value: nextOptionValue(options) },
            ])
          }
        >
          <PlusIcon aria-hidden="true" />
          Add option
        </AdminButton>
      </fieldset>
    </div>
  )
}
